/**
 * BattleFx.tsx
 * Renders short-lived projectile and impact effects on the battle grid.
 * Modeled on BattleEffects.tsx — uses a flat list of active FX entries
 * that auto-expire. Projectile entries slide from attacker to target tile,
 * then an impact burst fires at the target position.
 */
import React, { useState, useEffect } from 'react';
import { getFxSprite } from '../utils/spriteMap';

export interface BattleFxEntry {
  id: string;
  type: 'projectile' | 'impact';
  skillOrAction: string;
  /** Grid tile x,y of the TARGET (both projectile and impact use this) */
  targetX: number;
  targetY: number;
  /** Pixel origin of ATTACKER tile center (for projectile start position) */
  fromPixelX?: number;
  fromPixelY?: number;
  duration?: number;
}

interface BattleFxProps {
  entries: BattleFxEntry[];
  tileSize: number;                 // px size of one grid tile (used to convert grid→pixel)
  onComplete: (id: string) => void;
}

const BattleFx: React.FC<BattleFxProps> = ({ entries, tileSize, onComplete }) => {
  const [activeEntries, setActiveEntries] = useState<BattleFxEntry[]>([]);

  useEffect(() => {
    setActiveEntries(entries);
    entries.forEach((entry) => {
      const duration = entry.duration ?? (entry.type === 'projectile' ? 450 : 300);
      setTimeout(() => onComplete(entry.id), duration);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  return (
    <div className="battle-fx-container" aria-hidden="true">
      {activeEntries.map((entry) => {
        const sprite = getFxSprite(entry.skillOrAction);
        const toPx   = entry.targetX * tileSize + tileSize / 2;
        const toPy   = entry.targetY * tileSize + tileSize / 2;

        if (entry.type === 'projectile' && entry.fromPixelX !== undefined && entry.fromPixelY !== undefined) {
          const dx = toPx - entry.fromPixelX;
          const dy = toPy - entry.fromPixelY;
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          return (
            <img
              key={entry.id}
              src={sprite}
              alt=""
              className="battle-fx battle-fx--projectile"
              style={{
                left: entry.fromPixelX,
                top:  entry.fromPixelY,
                '--fx-dx': `${dx}px`,
                '--fx-dy': `${dy}px`,
                transform: `rotate(${angle}deg)`,
                animationDuration: `${entry.duration ?? 450}ms`,
              } as React.CSSProperties}
            />
          );
        }

        // impact burst
        return (
          <img
            key={entry.id}
            src={sprite}
            alt=""
            className="battle-fx battle-fx--impact"
            style={{
              left: toPx,
              top:  toPy,
              animationDuration: `${entry.duration ?? 300}ms`,
            }}
          />
        );
      })}
    </div>
  );
};

export default BattleFx;
